// Game rules: the clock, the seasons, the weather, tool actions, crop growth
// overnight, the economy and saving. Everything that decides *what happens*
// lives here; the systems it leans on (audio, fishing, animals, villagers,
// achievements, saves) are one file each beside it.
import type * as pc from "playcanvas";
import type { Atlas } from "../render/atlas.ts";
import { Particles } from "../render/fx.ts";
import { Scene, type CursorKind } from "../render/scene.ts";
import { Achievements } from "./achievements.ts";
import { Animals } from "./animals.ts";
import { Audio } from "./audio.ts";
import {
  ANIMALS,
  ANIMAL_FEED_COST,
  AUTOSAVE_SECONDS,
  BUILD_BY_ID,
  CAN_CAPACITY,
  CROPS,
  CROP_BY_ID,
  CROW_CHANCE,
  DAY_END_HOUR,
  DAY_START_HOUR,
  ENERGY_MAX,
  FAINT_ENERGY_REFUND,
  FAINT_GOLD_PENALTY,
  FERTILISERS,
  FORAGE_PER_DAY,
  FORAGE_TARGET,
  HOUR_SECONDS,
  MINE_FLOORS,
  REACH_TILES,
  REGION_BY_ID,
  SCARECROW_RADIUS,
  SEASONS,
  SEASON_DAYS,
  SEASON_NAMES,
  SEASON_TINT,
  SOIL_FALLOW_GAIN,
  SOIL_HARVEST_DRAIN,
  SOIL_REPEAT_DRAIN,
  SOIL_START,
  START_GOLD,
  TIERS,
  TIER_BY_ID,
  TOOLS,
  TREE_WOOD,
  WEATHER,
  WEATHER_ODDS,
  WEED_CHANCE,
  qualityOdds,
  seasonOf,
  type AnimalKind,
  type SeasonId,
  type TierId,
  type ToolDef,
  type ToolId,
  type WeatherId,
} from "./config.ts";
import { Fishing } from "./fishing.ts";
import { Hud, isSellable, itemInfo, type LedgerEntry } from "./hud.ts";
import { DEFAULT_BINDINGS, Input, type Action } from "./input.ts";
import { Npcs } from "./npc.ts";
import { Player } from "./player.ts";
import { SaveStore } from "./save.ts";
import { CloudSaves, signOut, type Account } from "./cloud.ts";
import { FORAGE_PROPS, Prop, Tile, World, hash2, makeCrop } from "./world.ts";

const SAVE_VERSION = 2;
const FADE_TIME = 0.55;
/** how many sellable items before the farmer switches to the hauling walk */
const HEAVY_SATCHEL = 14;
/** how often falling rain sweeps the farm for dry soil */
const RAIN_TICK_SECONDS = 1;

export class Game {
  readonly world = new World();
  readonly player: Player;
  readonly scene: Scene;
  readonly hud: Hud;
  readonly input: Input;
  readonly audio = new Audio();
  readonly particles = new Particles();
  readonly fishing = new Fishing();
  readonly animals = new Animals();
  readonly npcs = new Npcs();
  readonly achievements = new Achievements();
  private readonly store = new SaveStore();
  private readonly fade: HTMLElement;
  /** who is playing, and whether their farm is being synced */
  readonly account: Account;

  gold = START_GOLD;
  day = 1;
  hour = DAY_START_HOUR;
  water = CAN_CAPACITY;
  energy = ENERGY_MAX;
  toolIndex = 0;
  seedId: string | null = "carrot";
  weather: WeatherId = "clear";
  forecast: WeatherId = "clear";
  readonly inventory = new Map<string, number>();
  /** what is waiting in the shipping crate, paid out at dawn */
  readonly crate = new Map<string, number>();
  readonly toolTiers: Record<string, TierId> = {};

  private buildId: string | null = null;
  private elapsed = 0;
  private sinceSave = 0;
  private sinceRainTick = 0;
  private sleeping: { t: number; done: boolean; collapsed: boolean } | null = null;
  private savingOff = false;
  private ledger: LedgerEntry[] = [];
  private flash = 0;
  private cursor: { x: number; y: number; kind: CursorKind } | null = null;

  constructor(
    app: pc.AppBase,
    atlas: Atlas,
    hudRoot: HTMLElement,
    fade: HTMLElement,
    account: Account = { signedIn: false, authRequired: false, sync: false },
  ) {
    this.account = account;
    // The cloud tier has to be attached before boot() reads a slot, or the
    // first load would see only the browser's copy and then be overwritten by it.
    if (account.sync) this.store.useCloud(new CloudSaves());
    this.fade = fade;
    this.player = new Player(this.world.home.spawn.x, this.world.home.spawn.y);
    this.scene = new Scene(app, atlas, this.world);
    // the hud reads key bindings while it builds the settings panel
    this.input = new Input(window, app.graphicsDevice.canvas);
    this.hud = new Hud(hudRoot, atlas, this);

    for (const t of TOOLS) this.toolTiers[t.id] = "basic";
    this.inventory.set("seed:carrot", 8);
    this.inventory.set("seed:wheat", 5);
    this.npcs.spawnDefaults(this.world);
    this.applySeason();

    void this.boot();

    window.addEventListener("beforeunload", () => {
      this.save();
      // The write above reaches localStorage synchronously but cannot finish a
      // network round trip; the beacon is what carries this last save to the
      // server after the tab is gone.
      this.store.flush();
    });
    if (matchMedia("(pointer: coarse)").matches) {
      this.hud.enableTouch((action, down) => this.input.setVirtual(action, down));
    }
    this.hud.syncVolumeSliders(this.audio.masterVolume, this.audio.sfxVolume, this.audio.musicVolume);
  }

  private async boot(): Promise<void> {
    const legacy = this.store.migrateLegacy();
    // a wedged store must never leave the player staring at a dead screen
    const stored = await this.store.read(this.store.activeSlot).catch(() => null);
    const data = stored ?? legacy;
    if (data && this.load(data)) {
      this.applySeason();
    } else {
      this.hud.open("help");
      this.forecast = this.rollWeather(seasonOf(1), 1);
    }
    if (!this.npcs.request) this.npcs.rollRequest(this.day, this.world.season);
    this.scene.markGroundDirty();
    this.scene.markPropsDirty();
  }

  // ------------------------------------------------------------------ frame

  update(dt: number): void {
    this.elapsed += dt;
    this.sinceSave += dt;
    this.input.pollGamepad();
    this.input.blocked = this.hud.modalOpen || this.sleeping !== null || this.hud.dialogueOpen;

    this.handleHotkeys();
    // whichever way the conversation ended — paged out, escaped, a panel over
    // the top of it — the villager is free to go again
    if (!this.hud.dialogueOpen) this.npcs.talking = null;

    if (this.sleeping) {
      this.updateSleep(dt);
    } else if (!this.hud.modalOpen && !this.hud.dialogueOpen) {
      this.hour += dt / HOUR_SECONDS;
      if (this.hour >= DAY_END_HOUR) {
        this.hud.toast("You collapse in the field…", "bad");
        this.audio.play("faint");
        this.beginSleep(true);
      }
    }

    this.updateFishing(dt);

    const move = this.input.blocked || this.fishing.active ? { x: 0, y: 0 } : this.input.axis();
    const wasMoving = this.player.moving;
    this.player.speedScale = this.satchelWeight() >= HEAVY_SATCHEL ? 0.78 : 1;
    this.player.stance = this.fishing.phase === "reeling"
      ? "reel"
      : this.fishing.active
        ? "fish"
        : this.player.speedScale < 1
          ? "carry"
          : "normal";
    this.player.update(dt, move, this.world);
    if (this.player.moving) this.footstep();
    else if (wasMoving) this.audio.step("grass", -99); // reset the cadence

    this.updateTargeting();
    this.handleUse();

    this.animals.update(dt, this.world, () => this.audio.play("cluck", 4));
    this.npcs.update(dt, this.world, this.player);
    this.particles.update(dt);
    this.ambientLife(dt);
    this.rainWatering(dt);

    this.scene.focus(this.player.x, this.player.y);
    this.scene.setDaylight(this.light());
    this.draw();

    this.hud.update({
      day: this.day,
      season: this.world.season,
      clock: formatClock(this.hour),
      gold: this.gold,
      toolIndex: this.toolIndex,
      toolTiers: this.toolTiers,
      seedId: this.seedId,
      water: this.water,
      waterMax: CAN_CAPACITY,
      energy: this.energy,
      inventory: this.inventory,
      crate: this.crate,
      prompt: this.prompt(),
      weather: this.weather,
      forecast: this.forecast,
      buildId: this.buildId,
      reel: this.fishing.reel,
      requestText: this.requestText(),
      animals: this.animals.list.map((a) => ({ name: a.name, kind: a.kind, affection: a.affection, fed: a.fed })),
      ownedRegions: this.world.owned,
      achievements: this.achievements.list(),
      mineFloor: this.world.onFarm ? 0 : this.world.mineFloor,
    });

    if (this.sinceSave > AUTOSAVE_SECONDS) {
      this.sinceSave = 0;
      this.save();
      // Anything an earlier push could not land — a tunnel, a restart — goes up
      // with this one.
      this.store.retrySync();
    }
    this.input.endFrame();
  }

  private draw(): void {
    this.scene.beginFrame();

    // the tile you are about to work
    if (this.cursor) {
      const pulse = 0.35 + 0.25 * Math.sin(this.elapsed * 6);
      this.scene.cursorAt(this.cursor.x, this.cursor.y, this.cursor.kind, pulse);
    }

    // the open doorway, once you are standing on the mat
    const door = this.world.findHouseDoor();
    if (door && this.world.onFarm) {
      const onMat = Math.abs(this.player.x - (door.x + 0.5)) < 1 && Math.abs(this.player.y - (door.y + 1.5)) < 1;
      if (onMat) this.scene.actor("house_door_open", door.x + 0.5, door.y + 1, { z: zAbove(door.y + 1) });
    }

    for (const a of this.animals.list) {
      if (!this.world.onFarm) break;
      this.scene.shadow(a.x, a.y, ANIMALS[a.kind].shadow, 0.7);
      this.scene.actor(this.animals.spriteOf(a), a.x, a.y);
    }
    for (const n of this.npcs.list) {
      if (!this.world.onFarm) break;
      this.scene.shadow(n.x, n.y, 0.8, 0.7);
      this.scene.actor(this.npcs.spriteOf(n), n.x, n.y);
    }

    this.scene.shadow(this.player.x, this.player.y, 0.85);
    this.scene.actor(this.player.spriteId(), this.player.x, this.player.y + 3 / 24);

    if (this.fishing.active) {
      const b = this.fishing.bobber;
      this.scene.actor("fx_bobber", b.x, b.y + this.fishing.bobOffset(this.elapsed), { z: zAbove(b.y) });
      if (this.fishing.wantsRipple(this.elapsed)) this.particles.emit("ripple", b.x, b.y);
    }

    // the build ghost, tinted by whether it would fit
    if (this.buildId && this.cursor) {
      const def = BUILD_BY_ID.get(this.buildId);
      if (def) {
        const ok = this.canBuildAt(this.cursor.x, this.cursor.y);
        this.scene.actor(def.sprite, this.cursor.x + 0.5, this.cursor.y + 1, {
          tint: ok ? [0.7, 1, 0.7] : [1, 0.55, 0.5],
          glow: 0.2,
          z: zAbove(this.cursor.y + 1),
        });
      }
    }

    this.particles.draw(this.scene);
    this.drawWeather();
    this.drawLamps();
    this.scene.endFrame();
  }

  private drawWeather(): void {
    if (!this.world.onFarm) return;
    const w = this.weather;
    if (w === "rain" || w === "storm") {
      const frame = Math.floor(this.elapsed * 14) % 4;
      this.scene.weatherSheet(`fx_rain_${frame}`, this.elapsed * 7, w === "storm" ? 1 : 0.9);
    } else if (w === "snow") {
      const frame = Math.floor(this.elapsed * 5) % 4;
      this.scene.weatherSheet(`fx_snow_${frame}`, this.elapsed * 1.2, 1);
    }
    if (this.flash > 0) this.scene.fullScreenFlash(Math.min(0.7, this.flash));
  }

  /** Warm pools under the windows once the sun is down. */
  private drawLamps(): void {
    if (!this.world.onFarm) return;
    const night = this.hour >= 19 || this.hour < 6.5;
    if (!night) return;
    const door = this.world.findHouseDoor();
    if (door) this.scene.fxSprite("fx_lamp_glow", door.x + 0.5, door.y - 0.6, 0.9);
    const shop = this.world.home.shop;
    this.scene.fxSprite("fx_lamp_glow", shop.x + 0.5, shop.y - 0.4, 0.75);
  }

  /**
   * Rain does the watering as it falls, not only at dawn: soil you hoe and
   * seeds you sow during a downpour come up wet within the second, so the can
   * stays on your belt all day. The dawn pass still runs — this one only ever
   * catches what was turned over since.
   */
  private rainWatering(dt: number): void {
    if (!WEATHER[this.weather].waters) return;
    this.sinceRainTick += dt;
    if (this.sinceRainTick < RAIN_TICK_SECONDS) return;
    this.sinceRainTick = 0;
    // rain falls on the farm whether or not you are standing in it
    const farm = this.world.layer("farm");
    let wet = 0;
    for (let i = 0; i < farm.tiles.length; i++) {
      if (farm.tiles[i] !== Tile.SoilDry) continue;
      farm.tiles[i] = Tile.SoilWet;
      wet++;
    }
    for (const c of farm.crops.values()) c.watered = true;
    if (wet && this.world.onFarm) this.scene.markGroundDirty();
  }

  /** Butterflies by day, the odd firefly at dusk. Pure ambience. */
  private ambientLife(dt: number): void {
    if (!this.world.onFarm || this.weather === "rain" || this.weather === "storm") return;
    if (Math.random() > dt * 0.5) return;
    const v = this.scene.visibleTiles();
    const x = v.x0 + Math.random() * (v.x1 - v.x0);
    const y = v.y0 + Math.random() * (v.y1 - v.y0);
    if (this.world.tile(Math.floor(x), Math.floor(y)) !== Tile.Grass) return;
    this.particles.emit("butterfly", x, y, { loop: true, alpha: 0.9 });
  }

  // ---------------------------------------------------------------- input

  private handleHotkeys(): void {
    // A conversation owns the keyboard, so the keys that page through it have
    // to be read past `blocked` — otherwise the box opens and nothing on earth
    // will shift it, which looks exactly like the game has frozen.
    if (this.hud.dialogueOpen) {
      if (this.input.fired("close")) {
        this.hud.closeDialogue();
      } else if (this.input.firedRaw("use") || this.input.firedRaw("interact") || this.input.clicked > 0) {
        this.hud.advanceDialogue();
        this.audio.play("ui", 2);
      }
      return;
    }
    if (this.input.fired("close")) {
      if (this.buildId) this.chooseBuild(null);
      else if (this.fishing.active) this.fishing.cancel();
      else this.hud.close();
    }
    if (this.input.fired("help")) this.hud.toggle("help");
    if (this.input.fired("inventory")) this.hud.toggle("inventory");
    if (this.input.fired("settings")) this.hud.toggle("settings");
    if (this.input.fired("build")) this.hud.toggle("build");

    if (!this.hud.modalOpen && !this.sleeping) {
      for (let i = 0; i < TOOLS.length; i++) {
        if (this.input.fired(`tool${i + 1}` as Action)) this.selectTool(i);
      }
      if (this.input.fired("nextTool")) this.selectTool((this.toolIndex + 1) % TOOLS.length);
      if (this.input.fired("prevTool")) this.selectTool((this.toolIndex + TOOLS.length - 1) % TOOLS.length);
      if (this.input.wheel !== 0) this.selectTool((this.toolIndex + this.input.wheel + TOOLS.length) % TOOLS.length);
      if (this.input.fired("cycleSeed")) this.cycleSeed();
      if (this.input.fired("interact")) this.interact();
    }
    // right-click swaps to whatever the tile under the cursor wants
    if (this.input.rightClicked > 0 && this.cursor) {
      if (this.buildId) this.chooseBuild(null);
      else this.autoPickTool(this.cursor.x, this.cursor.y);
    }
  }

  /** Where the tool would land: the pointer if it is in reach, else straight ahead. */
  private updateTargeting(): void {
    if (this.sleeping || this.hud.modalOpen) {
      this.cursor = null;
      return;
    }
    let target = this.player.facingTile();
    let fromPointer = false;
    // a click is a pointer action whether or not the mouse has moved recently
    const pointing = this.input.pointer.active || this.input.clicked > 0 || this.input.rightClicked > 0;
    if (pointing) {
      const t = this.tileUnderPointer();
      if (t) {
        target = t;
        fromPointer = true;
      }
    }
    if (!this.world.inBounds(target.x, target.y)) {
      this.cursor = null;
      return;
    }
    const kind: CursorKind = this.buildId
      ? this.canBuildAt(target.x, target.y)
        ? "hand"
        : "blocked"
      : fromPointer
        ? this.applyTool(TOOLS[this.toolIndex]!, target.x, target.y, true)
          ? "hand"
          : "blocked"
        : "plain";
    this.cursor = { x: target.x, y: target.y, kind };
  }

  private handleUse(): void {
    if (this.input.blocked || this.sleeping) return;
    // A click carries its own target: read the tile straight back off the
    // pointer rather than trusting the cursor the last frame happened to leave
    // behind. Keys fall back to whatever the farmer is facing.
    const clickTile = this.input.clicked > 0 ? this.tileUnderPointer() : null;
    const pressed = this.input.fired("use");
    if (!clickTile && !pressed) return;

    const target = clickTile ?? this.cursor ?? this.player.facingTile();
    if (this.buildId) {
      this.placeBuild(target.x, target.y);
      return;
    }
    if (this.fishing.active) return; // the fishing update owns the key
    if (this.player.busy) return;

    if (clickTile) {
      this.player.faceTile(clickTile.x, clickTile.y);
      this.useTool(clickTile);
    } else {
      this.useTool(this.player.facingTile());
    }
  }

  /** The tile the mouse is over, if it is close enough to reach. */
  private tileUnderPointer(): { x: number; y: number } | null {
    const t = this.scene.tileAtScreen(this.input.pointer.x, this.input.pointer.y);
    if (!this.world.inBounds(t.x, t.y)) return null;
    const d = Math.hypot(t.x + 0.5 - this.player.x, t.y + 0.5 - this.player.y);
    return d <= REACH_TILES ? t : null;
  }

  private autoPickTool(x: number, y: number): void {
    const wants: ToolId | null = this.world.cropAt(x, y)?.stage === 3
      ? "shears"
      : this.world.isSoil(x, y)
        ? this.world.cropAt(x, y)
          ? "wateringcan"
          : "seedbag"
        : this.world.prop(x, y) === Prop.TreeA || this.world.prop(x, y) === Prop.TreeB
          ? "axe"
          : this.world.prop(x, y) >= Prop.Rock && this.world.prop(x, y) <= Prop.OreGold
            ? "pickaxe"
            : this.world.isOpenWater(x, y)
              ? "fishingrod"
              : this.world.isFarmable(x, y)
                ? "hoe"
                : null;
    if (!wants) return;
    const i = TOOLS.findIndex((t) => t.id === wants);
    if (i >= 0 && i !== this.toolIndex) {
      this.selectTool(i);
      this.audio.play("ui");
    }
  }

  private footstep(): void {
    const t = this.world.tile(Math.floor(this.player.x), Math.floor(this.player.y));
    const surface =
      t === Tile.Wood || t === Tile.Plank || t === Tile.Bridge
        ? "wood"
        : t === Tile.Stone || t === Tile.Gravel || t === Tile.CaveFloor
          ? "stone"
          : t === Tile.SoilWet
            ? "water"
            : "grass";
    this.audio.step(surface, this.elapsed);
  }

  // -------------------------------------------------------------- prompts

  private prompt(): string | null {
    if (this.sleeping) return null;
    if (this.buildId) return null;
    if (this.fishing.phase === "bite") return "Space — hook it!";
    if (this.fishing.phase === "waiting") return "waiting for a bite…";
    const at = this.player.tile();

    const npc = this.npcs.nearest(this.player.x, this.player.y);
    if (npc) return `E — talk to ${npc.name}`;
    const animal = this.animals.nearest(this.player.x, this.player.y);
    if (animal) return animal.fed ? `E — pet ${animal.name}` : `E — feed ${animal.name}`;
    if (this.nearProp(Prop.Bin)) return "E — open the shipping crate";
    if (this.nearProp(Prop.Shop)) return "E — the general store";
    if (this.nearProp(Prop.CaveEntrance)) return "E — go down into the mine";
    if (this.world.prop(at.x, at.y) === Prop.LadderDown) return "E — deeper";
    if (this.world.prop(at.x, at.y) === Prop.LadderUp) return this.world.mineFloor === 1 ? "E — back to the surface" : "E — climb up";
    if (this.onDoormat()) return "E — go to bed";
    if (this.world.prop(at.x, at.y) === Prop.Chest) return "E — chest";

    const t = this.player.facingTile();
    if (this.heldFertiliser() && this.world.isSoil(t.x, t.y) && this.world.fertility(t.x, t.y) < 60) {
      return `E — spread ${itemInfo(this.heldFertiliser()!).name}`;
    }
    if (this.world.isOpenWater(t.x, t.y) && TOOLS[this.toolIndex]!.id === "fishingrod") {
      return "Space — cast";
    }
    if (this.world.tile(t.x, t.y) === Tile.Water && this.water < CAN_CAPACITY) {
      return "2 then Space — refill the watering can";
    }
    return null;
  }

  private nearProp(p: Prop, r = 1.6): boolean {
    const at = this.player.tile();
    for (let dy = -2; dy <= 2; dy++) {
      for (let dx = -2; dx <= 2; dx++) {
        if (this.world.prop(at.x + dx, at.y + dy) !== p) continue;
        if (Math.abs(this.player.x - (at.x + dx + 0.5)) < r + 1 && Math.abs(this.player.y - (at.y + dy + 0.5)) < r + 1) {
          return true;
        }
      }
    }
    return false;
  }

  private onDoormat(): boolean {
    const door = this.world.findHouseDoor();
    if (!door) return false;
    return Math.abs(this.player.x - (door.x + 0.5)) < 1.1 && Math.abs(this.player.y - (door.y + 1.5)) < 1.1;
  }

  private interact(): void {
    // an open conversation never reaches here; handleHotkeys pages it instead
    const npc = this.npcs.nearest(this.player.x, this.player.y);
    if (npc) {
      this.audio.play("ui");
      const lines = this.npcs.talk(npc, this.day, this.world.season, this.weather);
      const req = this.npcs.request;
      if (npc.id === "keeper" && req && !req.filled && this.countCrop(req.cropId) >= req.qty) {
        this.takeCrop(req.cropId, req.qty);
        this.gold += req.reward;
        req.filled = true;
        lines.push(`${npc.name}: Perfect. Here — ${req.reward}g.`);
        this.audio.play("coin");
        this.floatAt(this.player.x, this.player.y, `+${req.reward}g`, "good");
      }
      this.hud.say(lines);
      this.npcs.talking = npc; // they stop where they are and turn to face you
      return;
    }

    const animal = this.animals.nearest(this.player.x, this.player.y);
    if (animal) {
      if (!animal.fed && (this.inventory.get("feed") ?? 0) > 0) {
        this.take("feed", 1);
        animal.fed = true;
        this.audio.play(animal.kind === "cow" ? "moo" : "cluck");
        this.hud.toast(`Fed ${animal.name}.`, "good");
      } else if (!animal.petted) {
        animal.petted = true;
        this.audio.play(animal.kind === "cow" ? "moo" : "cluck", 3);
        this.particles.emit("sparkle", animal.x, animal.y - 0.5, { count: 2 });
        this.hud.toast(`${animal.name} seems pleased.`, "good");
      } else {
        this.hud.toast(`${animal.name} has had enough attention.`);
      }
      return;
    }

    if (this.nearProp(Prop.Bin)) {
      this.hud.open("crate");
      return;
    }
    if (this.nearProp(Prop.Shop)) {
      this.hud.open("shop");
      return;
    }
    const facing = this.player.facingTile();
    if (this.spreadFertiliser(facing.x, facing.y)) return;

    const at = this.player.tile();
    if (this.world.prop(at.x, at.y) === Prop.LadderDown) {
      this.descend(this.world.mineFloor + 1);
      return;
    }
    if (this.world.prop(at.x, at.y) === Prop.LadderUp) {
      if (this.world.mineFloor <= 1) this.leaveMine();
      else this.descend(this.world.mineFloor - 1);
      return;
    }
    if (this.nearProp(Prop.CaveEntrance) && this.world.onFarm) {
      this.descend(1);
      return;
    }
    if (this.onDoormat()) {
      this.beginSleep(false);
      return;
    }
    this.hud.toast("Nothing to do here.");
  }

  // ---------------------------------------------------------------- tools

  private tierOf(tool: ToolDef): TierId {
    return tool.upgradable ? (this.toolTiers[tool.id] ?? "basic") : "basic";
  }

  private useTool(centre: { x: number; y: number }): void {
    const tool = TOOLS[this.toolIndex]!;
    if (!this.world.inBounds(centre.x, centre.y)) return;

    if (tool.id === "fishingrod") {
      this.tryCast(centre);
      return;
    }

    const tier = TIER_BY_ID.get(this.tierOf(tool))!;
    const cost = Math.round(tool.energy * tier.energy);
    // you can always take one more swing — it is the swing that finishes you
    if (this.energy <= 0) {
      this.hud.toast("Too tired. Eat something, or sleep.", "bad");
      this.audio.play("error");
      return;
    }

    const targets = areaAround(centre, tier.area, this.player.dir);
    let did = 0;
    for (const t of targets) {
      if (!this.world.inBounds(t.x, t.y)) continue;
      if (this.applyTool(tool, t.x, t.y, false)) did++;
    }
    if (did === 0) return;

    this.spend(cost);
    this.player.startAction(tool.anim, tool.duration * tier.speed);
  }

  /**
   * One tool on one tile. With `dry` set nothing changes — the cursor uses it to
   * decide whether to show the hand or the crossed-out bracket.
   */
  private applyTool(tool: ToolDef, x: number, y: number, dry: boolean): boolean {
    const world = this.world;
    switch (tool.id) {
      case "hoe": {
        if (world.cropAt(x, y) || !world.isFarmable(x, y)) return false;
        if (dry) return true;
        world.setTile(x, y, Tile.SoilDry);
        if (world.fertility(x, y) === 0) world.setFertility(x, y, SOIL_START);
        this.scene.markGroundDirty();
        this.particles.emit("dirt", x + 0.5, y + 0.5);
        this.audio.play("hoe");
        return true;
      }

      case "wateringcan": {
        // soil first: a plot next to the pond should still be waterable
        if (!world.isSoil(x, y)) {
          if (world.tile(x, y) === Tile.Water || world.nearWater(x, y)) {
            if (dry) return this.water < CAN_CAPACITY;
            if (this.water >= CAN_CAPACITY) return false;
            this.water = CAN_CAPACITY;
            this.hud.toast("Watering can refilled.", "good");
            this.particles.emit("splash", x + 0.5, y + 0.5);
            this.audio.play("water");
            return true;
          }
          return false;
        }
        if (this.water <= 0) {
          if (!dry) {
            this.hud.toast("The can is empty — refill it at the pond.", "bad");
            this.audio.play("error");
          }
          return false;
        }
        if (world.tile(x, y) === Tile.SoilWet) return false;
        if (dry) return true;
        this.water--;
        world.setTile(x, y, Tile.SoilWet);
        const crop = world.cropAt(x, y);
        if (crop) crop.watered = true;
        this.scene.markGroundDirty();
        this.particles.emit("splash", x + 0.5, y + 0.5);
        this.audio.play("water");
        return true;
      }

      case "seedbag": {
        if (!this.seedId) return false;
        if (!world.isSoil(x, y) || world.cropAt(x, y)) return false;
        const key = `seed:${this.seedId}`;
        if ((this.inventory.get(key) ?? 0) <= 0) {
          if (!dry) {
            this.hud.toast("Out of that seed.", "bad");
            this.cycleSeed();
          }
          return false;
        }
        const def = CROP_BY_ID.get(this.seedId)!;
        if (!def.seasons.includes(world.season) && !world.isGreenhouse(x, y)) {
          if (!dry) {
            this.hud.toast(`${def.name} will not grow in ${SEASON_NAMES[world.season]}.`, "bad");
            this.audio.play("error");
          }
          return false;
        }
        if (dry) return true;
        this.take(key, 1);
        // planting the same thing twice running takes it out of the soil
        if (world.active.last[world.idx(x, y)] === CROPS.findIndex((c) => c.id === this.seedId) + 1) {
          world.setFertility(x, y, world.fertility(x, y) - SOIL_REPEAT_DRAIN);
        }
        world.crops.set(world.idx(x, y), makeCrop({ id: this.seedId, watered: world.tile(x, y) === Tile.SoilWet }));
        this.floatAt(x, y, `−1 ${def.name} seed`, "info");
        this.scene.markPropsDirty();
        this.audio.play("plant");
        return true;
      }

      case "shears": {
        const crop = world.cropAt(x, y);
        if (crop && crop.stage === 3) {
          if (dry) return true;
          this.harvest(x, y, crop.id, crop.quality);
          const def = CROP_BY_ID.get(crop.id)!;
          const last = CROPS.findIndex((c) => c.id === crop.id) + 1;
          world.active.last[world.idx(x, y)] = last;
          world.setFertility(x, y, world.fertility(x, y) - SOIL_HARVEST_DRAIN);
          if (def.regrow) {
            crop.stage = 2;
            crop.progress = 0;
            crop.watered = false;
            crop.quality = 0;
          } else {
            world.crops.delete(world.idx(x, y));
          }
          this.scene.markPropsDirty();
          this.scene.markGroundDirty();
          return true;
        }
        // forage is picked with the same tool
        const forageKey = FORAGE_PROPS[world.prop(x, y)];
        if (forageKey) {
          if (dry) return true;
          this.give(forageKey, 1);
          const info = itemInfo(forageKey);
          this.floatAt(x, y, `+1 ${info.name}`, "good");
          world.setProp(x, y, world.prop(x, y) === Prop.BushBerry ? Prop.BushPicked : Prop.None);
          this.particles.emit("sparkle", x + 0.5, y + 0.5, { count: 2 });
          this.scene.markPropsDirty();
          this.audio.play("harvest");
          return true;
        }
        if (world.prop(x, y) === Prop.Weed) {
          if (dry) return true;
          world.setProp(x, y, Prop.None);
          this.particles.emit("leaves", x + 0.5, y + 0.5, { count: 2 });
          this.scene.markPropsDirty();
          return true;
        }
        return false;
      }

      case "axe": {
        const prop = world.prop(x, y);
        if (prop === Prop.TreeA || prop === Prop.TreeB) {
          if (dry) return true;
          world.setProp(x, y, Prop.Stump);
          this.give("wood", TREE_WOOD);
          this.floatAt(x, y, `+${TREE_WOOD} wood`, "good");
          this.particles.emit("leaves", x + 0.5, y - 0.5, { count: 5 });
          this.audio.play("chop");
          for (const id of this.achievements.bump("trees")) this.awardToast(id);
          this.scene.markPropsDirty();
          return true;
        }
        if (prop === Prop.Stump) {
          if (dry) return true;
          world.setProp(x, y, Prop.None);
          this.give("wood", 1);
          this.particles.emit("dirt", x + 0.5, y + 0.5);
          this.audio.play("chop");
          this.scene.markPropsDirty();
          return true;
        }
        if (prop === Prop.Weed || prop === Prop.Stick) {
          if (dry) return true;
          if (prop === Prop.Stick) this.give("forage:stick", 1);
          world.setProp(x, y, Prop.None);
          this.scene.markPropsDirty();
          return true;
        }
        const crop = world.cropAt(x, y);
        if (crop) {
          if (dry) return true;
          world.crops.delete(world.idx(x, y));
          this.hud.toast(crop.stage === 4 ? "Cleared the dead crop." : "Cleared the patch.");
          this.particles.emit("leaves", x + 0.5, y + 0.5, { count: 3 });
          this.scene.markPropsDirty();
          return true;
        }
        if (world.isSoil(x, y)) {
          if (dry) return true;
          world.setTile(x, y, Tile.Grass);
          world.setFertility(x, y, 0);
          this.scene.markGroundDirty();
          return true;
        }
        return false;
      }

      case "pickaxe": {
        const prop = world.prop(x, y);
        if (prop === Prop.Rock || prop === Prop.RockLarge) {
          if (dry) return true;
          world.setProp(x, y, Prop.None);
          const n = prop === Prop.RockLarge ? 3 : 1;
          this.give("forage:stone", n);
          this.floatAt(x, y, `+${n} stone`, "good");
          this.particles.emit("dirt", x + 0.5, y + 0.5, { count: 2 });
          this.audio.play("pickaxe");
          this.scene.markPropsDirty();
          return true;
        }
        if (prop === Prop.OreCopper || prop === Prop.OreIron || prop === Prop.OreGold) {
          if (dry) return true;
          const key = prop === Prop.OreGold ? "ore:gold" : prop === Prop.OreIron ? "ore:iron" : "ore:copper";
          world.setProp(x, y, Prop.None);
          this.give(key, 1);
          this.give("forage:stone", 1);
          this.floatAt(x, y, `+1 ${itemInfo(key).name}`, "good");
          this.particles.emit("sparkle", x + 0.5, y + 0.5, { count: 3 });
          this.audio.play("pickaxe");
          this.scene.markPropsDirty();
          return true;
        }
        if (world.tile(x, y) === Tile.Gravel || world.tile(x, y) === Tile.Plank) {
          if (dry) return true;
          world.setTile(x, y, Tile.Grass);
          this.scene.markGroundDirty();
          return true;
        }
        return false;
      }

      default:
        return false;
    }
  }

  /** The best fertiliser in the satchel, if any. */
  private heldFertiliser(): string | null {
    for (const f of [...FERTILISERS].reverse()) {
      if ((this.inventory.get(f.key) ?? 0) > 0) return f.key;
    }
    return null;
  }

  /** Work a sack into a tilled tile. Fertility is what quality rolls against. */
  private spreadFertiliser(x: number, y: number): boolean {
    const key = this.heldFertiliser();
    if (!key || !this.world.isSoil(x, y) || this.world.fertility(x, y) >= 60) return false;
    const def = FERTILISERS.find((f) => f.key === key)!;
    this.take(key, 1);
    this.world.setFertility(x, y, this.world.fertility(x, y) + def.boost);
    this.scene.markGroundDirty();
    this.particles.emit("dirt", x + 0.5, y + 0.5, { count: 2 });
    this.floatAt(x, y, `soil ${this.world.fertility(x, y)}`, "good");
    this.audio.play("plant");
    return true;
  }

  private harvest(x: number, y: number, cropId: string, quality: number): void {
    const def = CROP_BY_ID.get(cropId)!;
    const key = quality > 0 ? `crop:${cropId}@${quality}` : `crop:${cropId}`;
    this.give(key, 1);
    const info = itemInfo(key);
    this.floatAt(x, y, `+1 ${info.name}`, "good");
    this.particles.emit("sparkle", x + 0.5, y + 0.3, { count: quality > 0 ? 5 : 3 });
    this.audio.play("harvest", quality * 2);
    this.hud.toast(`Harvested ${info.name} (${info.price}g at the crate)`, "good");
    if (this.achievements.unlock("first_harvest")) this.awardToast("first_harvest");
    if (quality === 2 && this.achievements.unlock("green_thumb")) this.awardToast("green_thumb");
    void def;
  }

  // -------------------------------------------------------------- fishing

  private tryCast(target: { x: number; y: number }): void {
    if (this.fishing.active) return;
    if (!this.world.isOpenWater(target.x, target.y)) {
      this.hud.toast("Cast into open water.");
      return;
    }
    if (this.energy < 1) {
      this.hud.toast("Too tired to cast.", "bad");
      return;
    }
    this.spend(1);
    this.fishing.cast(target.x, target.y, this.player.dir);
    this.particles.emit("splash", target.x + 0.5, target.y + 0.5);
    this.audio.play("cast");
  }

  private updateFishing(dt: number): void {
    if (!this.fishing.active) return;
    const strike = this.input.fired("use") || this.input.clicked > 0;
    const hold = this.input.held("use") || this.input.clicked > 0;
    const wasBite = this.fishing.phase === "bite";
    const result = this.fishing.update(dt, hold, strike);
    if (!wasBite && this.fishing.phase === "bite") this.audio.play("bite");
    if (this.fishing.phase === "reeling" && Math.floor(this.elapsed * 8) % 2 === 0) this.audio.play("reel", 6);
    if (!result) return;

    if (result.kind === "caught" && result.fish) {
      const key = `fish:${result.fish.id}`;
      this.give(key, 1);
      this.floatAt(this.player.x - 0.5, this.player.y - 1, `+1 ${result.fish.name}`, "good");
      this.hud.toast(`Landed a ${result.fish.name}! (${result.fish.price}g)`, "good");
      this.particles.emit("sparkle", this.player.x, this.player.y - 1, { count: 5 });
      this.audio.play("splash");
      this.achievements.bump("fish");
      if (this.achievements.unlock("angler")) this.awardToast("angler");
      if (result.fish.id === "trout" && this.achievements.unlock("trout_tickler")) this.awardToast("trout_tickler");
    } else if (result.kind === "escaped") {
      this.hud.toast("It got away.", "bad");
      this.audio.play("error");
    }
  }

  // ---------------------------------------------------------------- build

  chooseBuild(id: string | null): void {
    this.buildId = id;
    if (id) this.hud.toast(`Placing ${BUILD_BY_ID.get(id)?.name ?? id} — Esc to stop.`);
  }

  private canBuildAt(x: number, y: number): boolean {
    const def = this.buildId ? BUILD_BY_ID.get(this.buildId) : null;
    if (!def) return false;
    if (!this.world.inBounds(x, y) || !this.world.owns(x, y)) return false;
    if (!this.canAfford(def.cost)) return false;
    if (def.water) return this.world.tile(x, y) === Tile.Water;
    if (def.kind === "tile") {
      const t = this.world.tile(x, y);
      return (t === Tile.Grass || t === Tile.Dirt) && this.world.prop(x, y) === Prop.None && !this.world.cropAt(x, y);
    }
    return this.world.fits(x, y, def.value as Prop);
  }

  private placeBuild(x: number, y: number): void {
    const def = this.buildId ? BUILD_BY_ID.get(this.buildId) : null;
    if (!def) return;
    if (!this.canBuildAt(x, y)) {
      this.audio.play("error");
      return;
    }
    this.payCost(def.cost);
    if (def.kind === "tile") {
      this.world.setTile(x, y, def.value as Tile);
      this.scene.markGroundDirty();
    } else {
      this.world.place(x, y, def.value as Prop);
      this.scene.markPropsDirty();
      if (def.value === Prop.Coop || def.value === Prop.Barn) {
        this.hud.toast(`${def.name} raised — buy stock at the store.`, "good");
      }
    }
    this.particles.emit("dirt", x + 0.5, y + 0.5, { count: 2 });
    this.audio.play("build");
    for (const id of this.achievements.bump("builds")) this.awardToast(id);
    if (!this.canAfford(def.cost)) {
      this.hud.toast("Out of materials.");
      this.buildId = null;
    }
  }

  private canAfford(cost: { gold?: number; wood?: number; stone?: number }): boolean {
    return (
      this.gold >= (cost.gold ?? 0) &&
      (this.inventory.get("wood") ?? 0) >= (cost.wood ?? 0) &&
      (this.inventory.get("forage:stone") ?? 0) >= (cost.stone ?? 0)
    );
  }

  private payCost(cost: { gold?: number; wood?: number; stone?: number }): void {
    this.gold -= cost.gold ?? 0;
    if (cost.wood) this.take("wood", cost.wood);
    if (cost.stone) this.take("forage:stone", cost.stone);
  }

  // ----------------------------------------------------------------- mine

  private descend(floor: number): void {
    if (floor > MINE_FLOORS) {
      this.hud.toast("The shaft bottoms out here.");
      if (this.achievements.unlock("spelunker")) this.awardToast("spelunker");
      return;
    }
    const start = this.world.generateMine(floor);
    this.world.enter("mine");
    this.player.x = start.x + 0.5;
    this.player.y = start.y + 1.5;
    this.scene.markGroundDirty();
    this.scene.markPropsDirty();
    this.scene.setClearColor(0.03, 0.03, 0.05);
    this.audio.setAmbience("cave");
    this.hud.toast(`Mine — floor ${floor}`);
    if (floor === MINE_FLOORS && this.achievements.unlock("spelunker")) this.awardToast("spelunker");
  }

  private leaveMine(): void {
    this.world.enter("farm");
    this.world.mineFloor = 0;
    const cave = this.world.home.cave;
    this.player.x = cave.x + 0.5;
    this.player.y = cave.y + 1.5;
    this.scene.markGroundDirty();
    this.scene.markPropsDirty();
    this.scene.setClearColor(0.07, 0.11, 0.09);
    this.updateAmbience(true);
  }

  // ------------------------------------------------------------------ day

  private beginSleep(collapsed: boolean): void {
    if (this.sleeping) return;
    this.sleeping = { t: 0, done: false, collapsed };
    this.fishing.cancel();
    this.audio.play(collapsed ? "faint" : "sleep");
    if (!collapsed) this.particles.emit("zzz", this.player.x, this.player.y - 1, { count: 3, ttl: 1.6 });
  }

  private updateSleep(dt: number): void {
    const s = this.sleeping!;
    s.t += dt;
    if (s.t < FADE_TIME) {
      this.fade.style.opacity = String(s.t / FADE_TIME);
      return;
    }
    if (!s.done) {
      s.done = true;
      this.fade.style.opacity = "1";
      this.nextDay(s.collapsed);
      return;
    }
    const out = (s.t - FADE_TIME) / FADE_TIME;
    this.fade.style.opacity = String(Math.max(0, 1 - out));
    if (out >= 1) {
      this.fade.style.opacity = "0";
      this.sleeping = null;
    }
  }

  nextDay(collapsed = false): void {
    const world = this.world;
    const wasSeason = world.season;
    this.day++;
    this.hour = DAY_START_HOUR;
    this.water = CAN_CAPACITY;
    this.energy = collapsed ? ENERGY_MAX * FAINT_ENERGY_REFUND : ENERGY_MAX;
    this.ledger = [];

    // today's weather was rolled last night, so the forecast could be honest
    this.weather = this.forecast;
    this.applySeason();
    this.forecast = this.rollWeather(seasonOf(this.day + 1), this.day + 1);

    if (world.season !== wasSeason) {
      this.ledger.push({ label: "The season turned", value: SEASON_NAMES[world.season] });
      if (this.day > SEASON_DAYS * SEASONS.length && this.achievements.unlock("four_seasons")) {
        this.awardToast("four_seasons");
      }
    }

    if (collapsed) {
      const lost = Math.round(this.gold * FAINT_GOLD_PENALTY);
      this.gold = Math.max(0, this.gold - lost);
      if (lost > 0) this.ledger.push({ label: "Carried home by a neighbour", value: `−${lost}g`, kind: "bad" });
    }

    const farm = world.layer("farm");
    const prev = world.active;
    world.active = farm;

    const rained = WEATHER[this.weather].waters;
    if (rained) {
      for (let i = 0; i < farm.tiles.length; i++) if (farm.tiles[i] === Tile.SoilDry) farm.tiles[i] = Tile.SoilWet;
      for (const c of farm.crops.values()) c.watered = true;
      this.ledger.push({ label: "Rain watered the farm", value: "free", kind: "good" });
    }

    // sprinklers reach their four neighbours
    let sprinkled = 0;
    for (let y = 0; y < world.h; y++) {
      for (let x = 0; x < world.w; x++) {
        if (world.prop(x, y) !== Prop.Sprinkler) continue;
        for (const [dx, dy] of [[0, -1], [1, 0], [0, 1], [-1, 0]] as [number, number][]) {
          if (!world.isSoil(x + dx, y + dy)) continue;
          world.setTile(x + dx, y + dy, Tile.SoilWet);
          const c = world.cropAt(x + dx, y + dy);
          if (c) c.watered = true;
          sprinkled++;
        }
      }
    }
    if (sprinkled) this.ledger.push({ label: "Sprinklers ran", value: `${sprinkled} tiles`, kind: "good" });

    // crows raid unguarded crops
    let eaten = 0;
    if (!rained && this.weather !== "snow") {
      for (const [i, crop] of [...farm.crops]) {
        if (crop.stage !== 3) continue;
        const x = i % world.w;
        const y = Math.floor(i / world.w);
        if (world.propWithin(x, y, SCARECROW_RADIUS, Prop.Scarecrow)) continue;
        if (hash2(i, this.day, 91) > CROW_CHANCE) continue;
        farm.crops.delete(i);
        eaten++;
      }
    }
    if (eaten) this.ledger.push({ label: "Crows got at the crops", value: `−${eaten}`, kind: "bad" });

    // growth
    let grown = 0;
    let died = 0;
    let frosted = 0;
    for (const [i, crop] of farm.crops) {
      const x = i % world.w;
      const y = Math.floor(i / world.w);
      const def = CROP_BY_ID.get(crop.id);
      if (!def) continue;
      const glass = world.isGreenhouse(x, y);
      if (crop.stage !== 4 && !glass && !def.seasons.includes(world.season)) {
        crop.stage = 4;
        frosted++;
        continue;
      }
      if (crop.stage === 4) continue;
      if (crop.watered) {
        crop.dry = 0;
        if (crop.stage < 3) {
          crop.progress++;
          if (crop.progress >= def.stageDays[crop.stage]!) {
            crop.progress = 0;
            crop.stage++;
            grown++;
            if (crop.stage === 3) crop.quality = rollQuality(world.fertility(x, y), i, this.day);
          }
        }
      } else if (crop.stage < 3) {
        crop.dry++;
        if (crop.dry >= 2) {
          crop.stage = 4;
          died++;
        }
      }
      crop.watered = false;
    }

    // soil dries, fallow ground recovers, stumps send up saplings, weeds creep in
    for (let i = 0; i < farm.tiles.length; i++) {
      if (farm.tiles[i] === Tile.SoilWet) farm.tiles[i] = Tile.SoilDry;
      if (farm.props[i] === Prop.Stump && hash2(i, this.day, 5) > 0.88) {
        farm.props[i] = hash2(i, this.day, 9) > 0.5 ? Prop.TreeA : Prop.TreeB;
      }
      const soil = farm.tiles[i] === Tile.SoilDry;
      if (soil && !farm.crops.has(i)) {
        farm.fert[i] = Math.min(100, farm.fert[i]! + SOIL_FALLOW_GAIN);
        if (farm.props[i] === Prop.None && hash2(i, this.day, 13) < WEED_CHANCE) farm.props[i] = Prop.Weed;
      }
      if (farm.props[i] === Prop.BushPicked && hash2(i, this.day, 17) > 0.7) farm.props[i] = Prop.BushBerry;
    }

    // the woods grow back
    const forage = world.countProp(Prop.BushBerry) + world.countProp(Prop.Mushroom) + world.countProp(Prop.Flower);
    if (forage < FORAGE_TARGET) world.scatterForage(FORAGE_PER_DAY, this.day);
    if (this.weather === "storm") this.stormDamage();

    // the crate pays out
    let paid = 0;
    for (const [key, n] of this.crate) paid += itemInfo(key).price * n;
    if (paid > 0) {
      this.gold += paid;
      this.crate.clear();
      this.ledger.push({ label: "The crate was collected", value: `+${paid}g`, kind: "good" });
      if (this.achievements.unlock("first_ship")) this.awardToast("first_ship");
    }

    // animals
    for (const y of this.animals.nextDay(world)) {
      this.give(y.key, y.count);
      const info = itemInfo(y.key);
      this.ledger.push({ label: `${info.name} from ${y.names.join(", ")}`, value: `+${y.count}`, kind: "good" });
      if (this.achievements.unlock("rancher")) this.awardToast("rancher");
    }

    if (grown) this.ledger.push({ label: "Crops grew a stage", value: `${grown}`, kind: "good" });
    if (died) this.ledger.push({ label: "Withered from thirst", value: `−${died}`, kind: "bad" });
    if (frosted) this.ledger.push({ label: "Lost to the change of season", value: `−${frosted}`, kind: "bad" });
    this.ledger.push({ label: "Today's weather", value: WEATHER[this.weather].name });

    world.active = prev;
    if (!world.onFarm) {
      // waking up always happens at home
      world.enter("farm");
      world.mineFloor = 0;
      this.scene.setClearColor(0.07, 0.11, 0.09);
    }

    this.npcs.rollRequest(this.day, world.season);
    this.player.x = this.world.home.spawn.x;
    this.player.y = this.world.home.spawn.y;
    this.player.stance = "normal";

    if (this.gold >= 10000 && this.achievements.unlock("moneybags")) this.awardToast("moneybags");
    this.achievements.bump("days");

    this.scene.markGroundDirty();
    this.scene.markPropsDirty();
    this.particles.clear();
    this.updateAmbience(true);
    this.audio.play("dawn");
    this.save();

    this.hud.showLedger(this.day, world.season, this.ledger);
  }

  /** A storm takes a few fence panels down and scatters branches. */
  private stormDamage(): void {
    const world = this.world;
    let broken = 0;
    let litter = 0;
    for (let i = 0; i < world.props.length; i++) {
      const p = world.props[i] as Prop;
      const isFence = p === Prop.FenceH || p === Prop.FenceV || p === Prop.FencePost || p === Prop.FencePostH;
      if (isFence && hash2(i, this.day, 41) > 0.985) {
        world.props[i] = Prop.None;
        broken++;
      } else if (p === Prop.None && hash2(i, this.day, 43) > 0.995 && world.tiles[i] === Tile.Grass) {
        world.props[i] = hash2(i, this.day, 45) > 0.5 ? Prop.Stick : Prop.StoneSmall;
        litter++;
      }
    }
    if (broken) this.ledger.push({ label: "The wind took some fencing", value: `−${broken}`, kind: "bad" });
    if (litter) this.ledger.push({ label: "Branches and stones blown down", value: `+${litter}`, kind: "good" });
  }

  private rollWeather(season: SeasonId, day: number): WeatherId {
    const roll = hash2(day, 777, 3);
    let acc = 0;
    for (const [id, p] of WEATHER_ODDS[season]) {
      acc += p;
      if (roll < acc) return id;
    }
    return "clear";
  }

  private applySeason(): void {
    const s = seasonOf(this.day);
    if (s !== this.world.season) {
      this.world.season = s;
      this.scene.markGroundDirty();
      this.scene.markPropsDirty();
    }
  }

  private updateAmbience(force = false): void {
    const night = this.hour >= 19.5 || this.hour < 6.5;
    const want = !this.world.onFarm
      ? "cave"
      : this.weather === "rain" || this.weather === "storm"
        ? "rain"
        : night
          ? "night"
          : "day";
    if (force || want !== this.lastAmbience) {
      this.lastAmbience = want;
      this.audio.setAmbience(want);
    }
    if (this.weather === "storm" && this.world.onFarm) {
      this.flash = Math.max(0, this.flash - 0.02);
      if (this.flash <= 0 && Math.random() < 0.0015) {
        this.flash = 0.7;
        this.audio.play("thunder");
      }
    } else {
      this.flash = 0;
    }
  }

  private lastAmbience = "";

  private light(): [number, number, number] {
    if (!this.world.onFarm) return [0.66, 0.64, 0.74];
    const base = daylight(this.hour);
    const s = SEASON_TINT[this.world.season];
    const w = WEATHER[this.weather].tint;
    this.updateAmbience();
    const flash = this.flash > 0.3 ? 0.45 : 0;
    return [
      Math.min(1.2, base[0] * s[0] * w[0] + flash),
      Math.min(1.2, base[1] * s[1] * w[1] + flash),
      Math.min(1.2, base[2] * s[2] * w[2] + flash),
    ];
  }

  // -------------------------------------------------------------- economy

  private give(key: string, n: number): void {
    this.inventory.set(key, (this.inventory.get(key) ?? 0) + n);
  }

  private take(key: string, n: number): boolean {
    const have = this.inventory.get(key) ?? 0;
    if (have < n) return false;
    this.inventory.set(key, have - n);
    return true;
  }

  private satchelWeight(): number {
    let n = 0;
    for (const [k, v] of this.inventory) if (isSellable(k)) n += v;
    return n;
  }

  private countCrop(cropId: string): number {
    let n = 0;
    for (const [k, v] of this.inventory) if (k === `crop:${cropId}` || k.startsWith(`crop:${cropId}@`)) n += v;
    return n;
  }

  private takeCrop(cropId: string, qty: number): void {
    let left = qty;
    for (const [k, v] of [...this.inventory]) {
      if (left <= 0) break;
      if (k !== `crop:${cropId}` && !k.startsWith(`crop:${cropId}@`)) continue;
      const used = Math.min(v, left);
      this.inventory.set(k, v - used);
      left -= used;
    }
  }

  private requestText(): string | null {
    const r = this.npcs.request;
    if (!r) return null;
    const crop = CROP_BY_ID.get(r.cropId);
    if (!crop) return null;
    return r.filled
      ? `Wren's order is filled. Come back tomorrow.`
      : `Wren wants ${r.qty} ${crop.name} today — ${r.reward}g, and you have ${this.countCrop(r.cropId)}.`;
  }

  private spend(energy: number): void {
    this.energy = Math.max(0, this.energy - energy);
    if (this.energy <= 0 && !this.sleeping) {
      this.hud.toast("You are out of energy.", "bad");
      this.player.stance = "faint";
      this.beginSleep(true);
    }
  }

  private floatAt(tileX: number, tileY: number, text: string, kind: "good" | "bad" | "info"): void {
    const p = this.scene.screenOf(tileX + 0.5, tileY);
    this.hud.float(text, p.x, p.y, kind);
  }

  private awardToast(id: string): void {
    const a = this.achievements.list().find((v) => v.id === id);
    if (!a) return;
    this.hud.toast(`Almanac — ${a.name}`, "good");
    this.audio.play("levelup");
  }

  // ------------------------------------------------------- hud callbacks

  buySeed(cropId: string, qty: number): void {
    const def = CROP_BY_ID.get(cropId);
    if (!def) return;
    const cost = def.seedCost * qty;
    if (this.gold < cost) {
      this.hud.toast("Not enough gold.", "bad");
      this.audio.play("error");
      return;
    }
    this.gold -= cost;
    this.give(`seed:${cropId}`, qty);
    if (!this.seedId || (this.inventory.get(`seed:${this.seedId}`) ?? 0) === 0) this.seedId = cropId;
    this.hud.toast(`Bought ${qty} ${def.name} seed${qty > 1 ? "s" : ""} (−${cost}g)`);
    this.audio.play("buy");
  }

  buyItem(key: string, qty: number): void {
    const price = key === "feed" ? ANIMAL_FEED_COST : (FERTILISERS.find((f) => f.key === key)?.cost ?? 0);
    const cost = price * qty;
    if (!price || this.gold < cost) {
      this.hud.toast("Not enough gold.", "bad");
      this.audio.play("error");
      return;
    }
    this.gold -= cost;
    this.give(key, qty);
    this.hud.toast(`Bought ${qty}× ${itemInfo(key).name} (−${cost}g)`);
    this.audio.play("buy");
  }

  /** Everything sellable goes into the crate; the crate pays out at dawn. */
  sellAll(): void {
    let moved = 0;
    for (const [key, count] of [...this.inventory]) {
      if (count <= 0 || !isSellable(key)) continue;
      this.crate.set(key, (this.crate.get(key) ?? 0) + count);
      this.inventory.set(key, 0);
      moved += count;
    }
    if (moved === 0) {
      this.hud.toast("Nothing to ship.");
      return;
    }
    this.hud.toast(`Loaded ${moved} into the crate — paid at dawn.`, "good");
    this.audio.play("build");
  }

  sellOne(key: string): void {
    if (!this.take(key, 1)) return;
    this.crate.set(key, (this.crate.get(key) ?? 0) + 1);
    this.audio.play("ui");
  }

  takeFromCrate(key: string): void {
    const have = this.crate.get(key) ?? 0;
    if (have <= 0) return;
    this.crate.set(key, have - 1);
    if (this.crate.get(key) === 0) this.crate.delete(key);
    this.give(key, 1);
    this.audio.play("ui");
  }

  eat(key: string): void {
    const info = itemInfo(key);
    if (info.food <= 0 || !this.take(key, 1)) return;
    this.energy = Math.min(ENERGY_MAX, this.energy + info.food);
    this.player.startAction("farmer_eat", 0.5, 2);
    this.hud.toast(`Ate ${info.name} (+${info.food} energy)`, "good");
    this.floatAt(this.player.x, this.player.y - 1, `+${info.food}`, "good");
    this.audio.play("eat");
  }

  upgradeTool(toolId: string): void {
    const tool = TOOLS.find((t) => t.id === toolId);
    if (!tool || !tool.upgradable) return;
    const at = TIERS.findIndex((t) => t.id === (this.toolTiers[toolId] ?? "basic"));
    const next = TIERS[at + 1];
    if (!next?.cost) return;
    const ore = [...this.inventory].filter(([k]) => k.startsWith("ore:")).reduce((a, [, v]) => a + v, 0);
    if (this.gold < next.cost.gold || (this.inventory.get("wood") ?? 0) < next.cost.wood || ore < next.cost.ore) {
      this.hud.toast("Not enough gold, wood or ore.", "bad");
      this.audio.play("error");
      return;
    }
    this.gold -= next.cost.gold;
    this.take("wood", next.cost.wood);
    let left = next.cost.ore;
    for (const [k, v] of [...this.inventory]) {
      if (left <= 0) break;
      if (!k.startsWith("ore:")) continue;
      const used = Math.min(v, left);
      this.inventory.set(k, v - used);
      left -= used;
    }
    this.toolTiers[toolId] = next.id;
    this.hud.toast(`${tool.name} is now ${next.name}.`, "good");
    this.audio.play("levelup");
  }

  buyAnimal(kind: AnimalKind): void {
    const def = ANIMALS[kind];
    const home = this.world.findProp(kind === "chicken" ? Prop.Coop : Prop.Barn);
    if (!home) {
      this.hud.toast(`Build a ${kind === "chicken" ? "coop" : "barn"} first.`, "bad");
      this.audio.play("error");
      return;
    }
    if (this.gold < def.cost) {
      this.hud.toast("Not enough gold.", "bad");
      this.audio.play("error");
      return;
    }
    this.gold -= def.cost;
    const a = this.animals.add(kind, home);
    this.hud.toast(`${a.name} the ${def.name.toLowerCase()} moves in.`, "good");
    this.audio.play(kind === "cow" ? "moo" : "cluck");
  }

  buyRegion(id: string): void {
    const r = REGION_BY_ID.get(id);
    if (!r || this.world.owned.has(id)) return;
    if (this.gold < r.price) {
      this.hud.toast("Not enough gold.", "bad");
      this.audio.play("error");
      return;
    }
    this.gold -= r.price;
    this.world.claimRegion(id);
    this.scene.markGroundDirty();
    this.scene.markPropsDirty();
    this.hud.toast(`${r.name} is yours.`, "good");
    this.audio.play("levelup");
    if (this.achievements.unlock("landowner")) this.awardToast("landowner");
  }

  selectTool(index: number): void {
    const next = Math.max(0, Math.min(TOOLS.length - 1, index));
    if (next !== this.toolIndex && this.fishing.active) this.fishing.cancel();
    this.toolIndex = next;
  }

  selectSeed(cropId: string): void {
    this.seedId = cropId;
  }

  private cycleSeed(): void {
    const owned = CROPS.filter((c) => (this.inventory.get(`seed:${c.id}`) ?? 0) > 0);
    if (owned.length === 0) {
      this.seedId = null;
      this.hud.toast("No seeds left — buy more at the store.", "bad");
      return;
    }
    const at = owned.findIndex((c) => c.id === this.seedId);
    const next = owned[(at + 1) % owned.length]!;
    this.seedId = next.id;
    this.hud.toast(`Selected ${next.name} seeds`);
    this.audio.play("ui");
  }

  setVolume(which: "master" | "sfx" | "music", value: number): void {
    this.audio.unlock();
    this.audio.setVolumes({ [which]: value });
  }

  toggleMute(): void {
    this.audio.setVolumes({ muted: !this.audio.muted });
  }

  setPixelScale(value: number): void {
    this.scene.setPixelScale(value === 0 ? null : value);
    try {
      localStorage.setItem("farmstead.scale.v1", String(value));
    } catch {
      /* storage disabled */
    }
  }

  rebind(action: Action): void {
    this.input.captureNextKey(action, () => this.hud.refreshBindings());
  }

  resetBindings(): void {
    this.input.resetBindings();
  }

  bindingLabel(action: Action): string {
    const codes = this.input.keyBindings[action] ?? DEFAULT_BINDINGS[action];
    return codes.map(prettyKey).join(" / ");
  }

  useSlot(slot: number): void {
    this.save();
    this.store.activeSlot = slot;
    location.reload();
  }

  eraseSlot(slot: number): void {
    void this.store.remove(slot);
  }

  /** One last save, then revoke centrally — not just locally — and start over. */
  async signOut(): Promise<void> {
    this.save();
    this.store.flush();
    await signOut();
    location.href = "/";
  }

  async slotSummaries(): Promise<{ slot: number; exists: boolean; day: number; gold: number; season: string }[]> {
    const rows = await this.store.list();
    return rows.map((r) => ({ ...r, season: r.season || "—" }));
  }

  // ------------------------------------------------------------------ save

  save(): void {
    if (this.savingOff) return;
    const farm = this.world.layer("farm");
    this.store.write(this.store.activeSlot, {
      v: SAVE_VERSION,
      day: this.day,
      hour: this.hour,
      gold: this.gold,
      water: this.water,
      energy: this.energy,
      season: SEASON_NAMES[this.world.season],
      weather: this.weather,
      forecast: this.forecast,
      seedId: this.seedId,
      toolIndex: this.toolIndex,
      toolTiers: { ...this.toolTiers },
      player: { x: this.player.x, y: this.player.y },
      inventory: [...this.inventory],
      crate: [...this.crate],
      owned: [...this.world.owned],
      animals: this.animals.toJSON(),
      npcs: this.npcs.toJSON(),
      awards: this.achievements.toJSON(),
      tiles: Array.from(farm.tiles),
      props: Array.from(farm.props),
      fert: Array.from(farm.fert),
      last: Array.from(farm.last),
      crops: [...farm.crops].map(([i, c]) => [i, c.id, c.stage, c.progress, c.watered ? 1 : 0, c.dry, c.quality]),
    });
  }

  private load(d: Record<string, unknown>): boolean {
    try {
      const v = Number(d.v ?? 0);
      if (v < 1) return false;
      const farm = this.world.layer("farm");
      this.day = Number(d.day ?? 1);
      this.hour = Number(d.hour ?? DAY_START_HOUR);
      this.gold = Number(d.gold ?? START_GOLD);
      this.water = Number(d.water ?? CAN_CAPACITY);
      this.energy = Number(d.energy ?? ENERGY_MAX);
      this.weather = (d.weather as WeatherId) ?? "clear";
      this.forecast = (d.forecast as WeatherId) ?? this.rollWeather(seasonOf(this.day + 1), this.day + 1);
      this.seedId = (d.seedId as string | null) ?? null;
      this.toolIndex = Number(d.toolIndex ?? 0);
      Object.assign(this.toolTiers, (d.toolTiers as Record<string, TierId>) ?? {});
      const p = d.player as { x: number; y: number } | undefined;
      if (p) {
        this.player.x = p.x;
        this.player.y = p.y;
      }
      this.inventory.clear();
      for (const [k, n] of (d.inventory as [string, number][]) ?? []) this.inventory.set(k, n);
      this.crate.clear();
      for (const [k, n] of (d.crate as [string, number][]) ?? []) this.crate.set(k, n);
      this.world.owned.clear();
      for (const id of (d.owned as string[]) ?? []) this.world.owned.add(id);
      this.animals.load((d.animals as never) ?? []);
      this.npcs.load((d.npcs as never) ?? {});
      this.achievements.load(d.awards as never);

      farm.tiles.set(d.tiles as ArrayLike<number>);
      farm.props.set(d.props as ArrayLike<number>);
      if (d.fert) farm.fert.set(d.fert as ArrayLike<number>);
      if (d.last) farm.last.set(d.last as ArrayLike<number>);
      farm.crops.clear();
      for (const row of (d.crops as unknown[][]) ?? []) {
        const [i, id, stage, progress, watered, dry, quality] = row as [number, string, number, number, number, number, number?];
        farm.crops.set(i, makeCrop({ id, stage, progress, watered: !!watered, dry, quality: quality ?? 0 }));
      }
      this.world.enter("farm");
      try {
        const scale = Number(localStorage.getItem("farmstead.scale.v1") ?? 0);
        if (scale > 0) this.scene.setPixelScale(scale);
      } catch {
        /* storage disabled */
      }
      return true;
    } catch {
      return false;
    }
  }

  /** Wipe this slot and start again from day one. */
  startOver(): void {
    this.savingOff = true; // or unloading the page would write the old farm back
    void this.store.remove(this.store.activeSlot);
    setTimeout(() => location.reload(), 60);
  }
}

/** Tiles a swing covers, laid out around the tile in front of the player. */
function areaAround(centre: { x: number; y: number }, area: 1 | 3 | 9, dir: string): { x: number; y: number }[] {
  if (area === 1) return [centre];
  if (area === 9) {
    const out: { x: number; y: number }[] = [];
    for (let dy = -1; dy <= 1; dy++) for (let dx = -1; dx <= 1; dx++) out.push({ x: centre.x + dx, y: centre.y + dy });
    return out;
  }
  const across = dir === "left" || dir === "right" ? { x: 0, y: 1 } : { x: 1, y: 0 };
  return [
    { x: centre.x - across.x, y: centre.y - across.y },
    centre,
    { x: centre.x + across.x, y: centre.y + across.y },
  ];
}

function rollQuality(fertility: number, seed: number, day: number): number {
  const [silver, gold] = qualityOdds(fertility);
  const r = hash2(seed, day, 63);
  if (r < gold) return 2;
  if (r < gold + silver) return 1;
  return 0;
}

function formatClock(hour: number): string {
  const h24 = Math.floor(hour) % 24;
  const minutes = Math.floor((hour % 1) * 60);
  const suffix = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${minutes.toString().padStart(2, "0")} ${suffix}`;
}

function prettyKey(code: string): string {
  if (code.startsWith("Key")) return code.slice(3);
  if (code.startsWith("Digit")) return code.slice(5);
  if (code.startsWith("Arrow")) return `${code.slice(5)} arrow`;
  return code;
}

/** Depth just in front of whatever stands on this row. */
function zAbove(tileY: number): number {
  return tileY * 0.01 + 0.004;
}

const LIGHT_KEYS: [number, [number, number, number]][] = [
  [DAY_START_HOUR, [0.82, 0.78, 0.9]],
  [8, [1, 0.98, 0.94]],
  [12, [1, 1, 1]],
  [17, [1, 0.96, 0.88]],
  [19.5, [1, 0.84, 0.68]],
  [21, [0.7, 0.68, 0.9]],
  [23, [0.55, 0.56, 0.85]],
  [DAY_END_HOUR, [0.48, 0.5, 0.8]],
];

function daylight(hour: number): [number, number, number] {
  const h = Math.max(DAY_START_HOUR, Math.min(DAY_END_HOUR, hour));
  for (let i = 1; i < LIGHT_KEYS.length; i++) {
    const [h1, c1] = LIGHT_KEYS[i]!;
    if (h <= h1) {
      const [h0, c0] = LIGHT_KEYS[i - 1]!;
      const t = (h - h0) / (h1 - h0);
      return [c0[0] + (c1[0] - c0[0]) * t, c0[1] + (c1[1] - c0[1]) * t, c0[2] + (c1[2] - c0[2]) * t];
    }
  }
  return LIGHT_KEYS[LIGHT_KEYS.length - 1]![1];
}
